import React, { useRef } from 'react';
import { Box, Autocomplete, TextField, IconButton, Typography } from '@mui/material';
import * as Icons from '@mui/icons-material';
import { useTheme } from '@mui/material';

/**
 * SearchBar component with autocomplete functionality
 * 
 * @param {Object} props
 * @param {string} props.searchInput - Current search input value
 * @param {Function} props.setSearchInput - Function to update search input
 * @param {boolean} props.open - Whether the suggestions dropdown is open
 * @param {Function} props.setOpen - Function to update open state
 * @param {boolean} props.isClearing - Whether the input is being cleared
 * @param {Array} props.suggestions - Array of suggestion objects
 * @param {Function} props.handleOptionSelect - Function to handle option selection
 * @param {Function} props.handleInputChange - Function to handle input changes
 * @param {Function} props.handleSearch - Function to handle search submission
 * @param {Function} props.handleKeyDown - Function to handle keyboard events
 */
const SearchBar = ({ 
  searchInput, 
  setSearchInput, 
  open, 
  setOpen, 
  isClearing,
  suggestions, 
  handleOptionSelect, 
  handleInputChange, 
  handleSearch,
  handleKeyDown
}) => {
  const theme = useTheme();
  const inputRef = useRef(null);

  return (
    <Box sx={{ width: '100%', maxWidth: 600 }}>
      <Autocomplete
        freeSolo
        clearOnBlur={false}
        blurOnSelect={false}
        value={null}
        options={suggestions}
        getOptionLabel={(option) => (typeof option === 'string' ? option : option.name)}
        inputValue={searchInput}
        onInputChange={handleInputChange}
        onChange={handleOptionSelect}
        filterOptions={(options) => options}
        open={open}
        onClose={() => {}}
        autoHighlight
        autoSelect={false}
        renderOption={(props, option, { selected }) => (
          <li {...props} style={option.disabled ? { pointerEvents: 'none', opacity: 0.5 } : {}}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {option.type === 'ingredient' ? (
                <Icons.Spa fontSize="small" sx={{ color: theme.palette.text.secondary }} />
              ) : option.type === 'recipe' ? (
                <Icons.LocalBar fontSize="small" sx={{ color: theme.palette.text.secondary }} />
              ) : null}
              <Typography
                variant="body2"
                color={option.disabled ? 'text.secondary' : 'text.primary'}
                sx={selected ? { fontWeight: 'bold', backgroundColor: 'action.selected' } : {}}
              >
                {option.name}
              </Typography>
            </Box>
          </li>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            inputRef={inputRef}
            placeholder="Search recipes or ingredients..."
            variant="outlined"
            fullWidth
            onKeyDown={handleKeyDown}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {params.InputProps.endAdornment}
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {searchInput && (
                      <IconButton
                        size="small"
                        onClick={() => {
                          setSearchInput('');
                          setOpen(false);
                        }}
                      >
                        <Icons.Clear fontSize="small" />
                      </IconButton>
                    )}
                    <IconButton size="small" onClick={handleSearch}>
                      <Icons.Search fontSize="small" />
                    </IconButton>
                  </Box>
                </>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 4,
                backgroundColor: theme.palette.background.paper,
              }
            }}
          />
        )}
        sx={{ width: '100%' }}
      />
    </Box>
  );
};

export default SearchBar; 